import {Component, OnInit, signal} from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule, ValidationErrors, ValidatorFn,
  Validators,
} from '@angular/forms';
import {MaterialModule} from "../../../../material.module";
import {AppCodeViewComponent} from "../../../../components/code-view/code-view.component";
import {NgIf, NgStyle} from "@angular/common";
import {TablerIconsModule} from "angular-tabler-icons";
import {AcceptedFileType, DivisionType, ExtractionMode, FileDecisions, FileTypes, Languages} from "../file";
import {FileUploadService} from "../../../../services/apps/file/file-upload.service";
import {ToastrService} from "ngx-toastr";
import {MatStepper} from "@angular/material/stepper";
import {AppFileUploadAreaComponent} from "./file-upload-area/file-upload-area.component";
import {STEPPER_GLOBAL_OPTIONS} from "@angular/cdk/stepper";
import {firstValueFrom} from "rxjs";
import {AuthorizationService} from "../../../../../core/authorization/AuthorizationService";
import {AuthInfoDTO} from "../../../../../core/dto/AuthInfoDTO";
import {environment} from "../../../../../environments/environment";
import {LocalizationPipe} from "../../../../pipe/localization-pipe";
import {LocalizationService} from "../../../../services/localizationService";
import {QuestionAnswer, QaInputComponent} from "./qa-input/qa-input.component";

@Component({
  selector: 'app-form-wizard',
  imports: [
    MaterialModule,
    FormsModule,
    ReactiveFormsModule,
    AppCodeViewComponent,
    NgIf,
    TablerIconsModule,
    NgStyle,
    AppFileUploadAreaComponent,
    QaInputComponent,
    LocalizationPipe
  ],
  templateUrl: './file-upload-form-wizard.component.html',
  styleUrls: ['./file-upload-form-wizard.component.scss'],
  providers: [
    {
      provide: STEPPER_GLOBAL_OPTIONS,
      useValue: { showError: true , }
    }
  ]
})
export class AppFileUploadFormWizardComponent implements OnInit {
  selectedOrganisationId:number;

  fileTypeFormGroup = this._formBuilder.group({
    fileTypeSelection: ['', Validators.required],
  });
  fileUploadFormGroup = this._formBuilder.group({
    uploadedFiles: [[] as File[], Validators.required],
    makeOcrForTables: this._formBuilder.group({
      type: [['pdf']],
      show: [false as boolean],
      value: [false as boolean, Validators.required],
    }),
    makeOcrForImages: this._formBuilder.group({
      type: [['pdf']],
      show: [false as boolean],
      value: [false as boolean, Validators.required],
    }),
    saveTablesAsImages: this._formBuilder.group({
      type: [['pdf']],
      show: [false as boolean],
      value: [false as boolean, Validators.required],
    }),
  });
  urlUploadFormGroup = this._formBuilder.group({
    url: ['' as string, [Validators.required, this.httpsValidator()]],
    language: [Languages.EN as Languages],
    length_low: [10 as number],
    length_high: [20 as number],
    stop_words_low: [0.0 as number],
    stop_words_high: [0.2 as number],
    mode: [ExtractionMode.AUTO as ExtractionMode],
    exclude_lines: [false as boolean, Validators.required],
    include_images: [false as boolean, Validators.required],
    include_formatting: [false as boolean, Validators.required],
  })
  multimediaUploadFormGroup = this._formBuilder.group({
    uploadedFiles: [[] as File[], Validators.required],
    includeImportantMoments: this._formBuilder.group({
      type: [['video']],
      show: [false as boolean],
      value: [false as boolean],
    }),
    hasTranscript: this._formBuilder.group({
      type: [['video']],
      show: [false as boolean],
      value: [false as boolean],
    }),
  })
  excelUploadFormGroup = this._formBuilder.group({
    uploadedFiles: [[] as File[], Validators.required],
    sheets: this._formBuilder.array([
      this._formBuilder.group({
        sheetTitleExists: this._formBuilder.group({
          type: [['excel']],
          show: [false as boolean],
          value: [false as boolean],
        }),
        updateSheetTitles: this._formBuilder.group({
          type: [['excel']],
          show: [false as boolean],
          value: [false as boolean],
        }),
        sheetIndex: this._formBuilder.control(null as number),
        sheetHeaders: this._formBuilder.array([
          this._formBuilder.group({
            key: this._formBuilder.control(''), // Excel dosyasında değişecek olan header'ın yeni ismi
            type: this._formBuilder.control('') // Filtre isteniyor ise filtrenin türü.
          })
        ])
      })
    ])
  });
  organizationUploadFormGroup = this._formBuilder.group({
    uploadedFiles: [[] as File[], Validators.required],
    organizationSheetName: ['' as string, Validators.required],
    columnMappings: this._formBuilder.group({}),
    divisionType: [null as DivisionType, Validators.required],
    divisionColumns: this._formBuilder.array([])
  })
  qaUploadFormGroup = this._formBuilder.group({
    qaData: this._formBuilder.control([] as QuestionAnswer[], Validators.required)
  })
  apiUploadFormGroup = this._formBuilder.group({})
  integrationUploadFormGroup = this._formBuilder.group({})

  fileUploadOptionsList = signal<FileTypes[]>([]);
  showSelectionFileTypeInput: boolean = false;
  userDecisions: FileDecisions = new FileDecisions();
  selectedFileType = null;
  resettingStepper: boolean = false;
  extractionModes = Object.keys(ExtractionMode)
  languages = Object.keys(Languages)
  isFileUploading: boolean = false;
  activeUser: AuthInfoDTO;
  maxFileSize: string;

  constructor(private _formBuilder: FormBuilder, private fileUploadService: FileUploadService, private toastService: ToastrService,
              private authorizationService: AuthorizationService, private localizationService: LocalizationService) {
    this.activeUser = this.authorizationService.getActiveUser();
    this.selectedOrganisationId = this.activeUser.organisationAccount.id;
    this.fileUploadOptionsList.set(this.fileUploadService.getFileUploadOptions());
    this.fileUploadService.getMaxFileSize().subscribe({
      next: (value: string) => {
        this.maxFileSize = value
      }
    })
    this.fileUploadService.getUsersFileOperationDecisions(this.selectedOrganisationId).subscribe({
      next: (res: any) => {
        const mapObject = {};
        if (Array.isArray(res.data) && res.data.length > 0) {
          for (let i: number = 0; i < res.data.length; i++) {
            const key: string = res.data[i].resourceSetting.name
            mapObject[key] = normalizeValue(res.data[i].value)
          }
        }
        function normalizeValue(value: any): any {
          if (typeof value === 'boolean' || typeof value === 'number') {
            return value;
          }
          if (value === 'true') return true;
          if (value === 'false') return false;
          const num = Number(value);
          if (!isNaN(num)) return num;
          return value;
        }
        this.userDecisions.bind(mapObject)
        this.userDecisions.mode = {
          type: "scraper",
          value: ExtractionMode.AUTO,
        }
        this.userDecisions.language = {
          type: "scraper",
          value: Languages.TR,
        }
        this.setUserDefaultDecisionsToFormGroups()
      },
      error: err => {
        this.toastService.error(localizationService.getLocalizationValue("FileUploadFormWizard.decisionsNotLoaded"))
        return
      }
    })
  }
  // TODO : Scraper denenecek ve yetki kodları eklenmeli scraper özelinde.
  ngOnInit(): void {
    this.setUserDefaultDecisionsToFormGroups()
  }

  setUserDefaultDecisionsToFormGroups() { // TODO : Verilerin ilk haline döndürülmesi gereken yerde bu metot çağırılmalı
    const sheetsArray = this.excelUploadFormGroup.get('sheets') as FormArray;
    if (sheetsArray && sheetsArray.length > 0) {
      sheetsArray.at(0).get('sheetTitleExists')?.get('value')?.setValue(false);
      sheetsArray.at(0).get('updateSheetTitles')?.get('value')?.setValue(false);
    }
    // console.log("Excel form group: ",this.excelUploadFormGroup)
    const keys: string[] = Object.keys(this.userDecisions);
    // console.log("Decision keys: ",keys)
    keys.forEach((key: string) => {
      if (this.userDecisions[key] && typeof this.userDecisions[key].value === "boolean") {
        switch (this.userDecisions[key].type) {
          case "video":
            this.multimediaUploadFormGroup.get(key)?.get('value')?.setValue(this.userDecisions[key].value)
            break;
          case "pdf":
            this.fileUploadFormGroup.get(key)?.get('value')?.setValue(this.userDecisions[key].value)
            break;
          // case "excel":
            // (this.excelUploadFormGroup.get('sheets') as FormArray).at(0).get(key).get('value')?.setValue(this.userDecisions[key].value)
            // break;
          case "scraper":
            this.urlUploadFormGroup.get(key)?.setValue(this.userDecisions[key].value)
            break;
        }
      }
    })
  }

  get anyFilesUploaded(): boolean {
    const fileUploadFiles = this.fileUploadFormGroup.get('uploadedFiles')?.value || [];
    const multimediaFiles = this.multimediaUploadFormGroup.get('uploadedFiles')?.value || [];
    const excelFiles = this.excelUploadFormGroup.get('uploadedFiles')?.value || [];
    const organizationFiles = this.organizationUploadFormGroup.get('uploadedFiles')?.value || [];
    const qaData = this.qaUploadFormGroup.get('qaData')?.value || [];

    return fileUploadFiles.length > 0 || multimediaFiles.length > 0 || excelFiles.length > 0 || organizationFiles.length > 0 || qaData.length > 0;
  }

  pickFormGroup(fileType){
    switch(fileType){
      case AcceptedFileType.DOCUMENT:
        return this.fileUploadFormGroup;
      case AcceptedFileType.SCRAPER:
        return this.urlUploadFormGroup;
      case AcceptedFileType.MULTIMEDIA:
        return this.multimediaUploadFormGroup;
      case AcceptedFileType.TABLE:
        return this.excelUploadFormGroup;
      case AcceptedFileType.ORGANIZATION:
        return this.organizationUploadFormGroup;
      case AcceptedFileType.QA:
        return this.qaUploadFormGroup;
      case AcceptedFileType.API:
        return this.apiUploadFormGroup;
      case AcceptedFileType.URL:
        return this.integrationUploadFormGroup;
      default:
          return this.fileUploadFormGroup;
    }
  }

  setFormGroupFile(files: File[], formGroupName: AcceptedFileType) {
    switch (formGroupName) {
      case AcceptedFileType.DOCUMENT:
        this.fileUploadFormGroup.get("uploadedFiles")?.setValue(files);
        this.fileUploadFormGroup.updateValueAndValidity();
        break;
      case AcceptedFileType.MULTIMEDIA:
        this.multimediaUploadFormGroup.get("uploadedFiles")?.setValue(files);
        this.multimediaUploadFormGroup.updateValueAndValidity();
        break;
      case AcceptedFileType.TABLE:
        this.excelUploadFormGroup.get("uploadedFiles")?.setValue(files);
        this.excelUploadFormGroup.updateValueAndValidity();
        break;
      case AcceptedFileType.ORGANIZATION:
        this.organizationUploadFormGroup.get("uploadedFiles")?.setValue(files);
        this.organizationUploadFormGroup.updateValueAndValidity();
        break;
      default:
        this.toastService.error(this.localizationService.getLocalizationValue("FileUploadFormWizard.enterAValidForm"))
        break;
    }

    // Form validasyonunu manuel olarak tetikle
    setTimeout(() => {
      const formGroup = this.pickFormGroup(formGroupName);
      if (formGroup) {
        formGroup.updateValueAndValidity();
      }
    }, 0);
  }

  changeUserDecision(updatedDecision: string, value: boolean, formGroupType: AcceptedFileType, sheetIndex: number){
    switch (formGroupType){
      case AcceptedFileType.DOCUMENT:
        this.fileUploadFormGroup.get(updatedDecision)?.get('value')?.setValue(value)
        break;
      case AcceptedFileType.MULTIMEDIA:
        this.multimediaUploadFormGroup.get(updatedDecision)?.get('value')?.setValue(value)
        break;
      case AcceptedFileType.TABLE:
        const sheetsArray = this.excelUploadFormGroup.get('sheets') as FormArray;
        if (sheetsArray && sheetsArray.length > sheetIndex) {
          sheetsArray.at(sheetIndex).get(updatedDecision)?.get('value')?.setValue(value)
          if (updatedDecision === 'updateSheetTitles' && value) {
            const sheetHeadersArray = sheetsArray.at(sheetIndex).get('sheetHeaders') as FormArray;
            for (let i = 0; i < sheetHeadersArray.length; i++) {
              sheetHeadersArray.at(i).get('key')?.setValidators([Validators.required]);
              sheetHeadersArray.at(i).get('type')?.setValidators([Validators.required]);
            }
          }
          if (updatedDecision === 'updateSheetTitles' && !value) {
            const sheetHeadersArray = sheetsArray.at(sheetIndex).get('sheetHeaders') as FormArray;
            for (let i = 0; i < sheetHeadersArray.length; i++) {
              sheetHeadersArray.at(i).get('key')?.clearValidators();
              sheetHeadersArray.at(i).get('type')?.clearValidators();
              sheetHeadersArray.at(i).get('key')?.updateValueAndValidity();
              sheetHeadersArray.at(i).get('type')?.updateValueAndValidity();
            }
          }
        }
        break;
      case AcceptedFileType.ORGANIZATION:
        this.organizationUploadFormGroup.get(updatedDecision)?.get('value')?.setValue(value)
        break;
      default:
        this.toastService.error(this.localizationService.getLocalizationValue("FileUploadFormWizard.enterAValidForm"))
        break
    }
  }

  showUserDecisionsByFileType(fileType: string, value: boolean, sheetIndex: number){
    switch (fileType){
      case "pdf":
        Object.keys(this.fileUploadFormGroup.controls).forEach(control => {
          if (!Array.isArray(this.fileUploadFormGroup.get(control)?.value)) {
            if ((((this.fileUploadFormGroup.get(control) as FormGroup)?.get('type') as any)?.value as string[])?.includes(fileType)) {
              this.fileUploadFormGroup.get(control)?.get('show')?.setValue(value)
            }
          }
        })
        break;
      case "table":
        const sheetsArray = this.excelUploadFormGroup.get('sheets') as FormArray;
        if (sheetsArray && sheetsArray.length > sheetIndex) {
          const firstSheetObject = sheetsArray.at(sheetIndex) as FormGroup
          const controls = firstSheetObject.controls;
          Object.keys(controls).forEach(control => {
            if (control === 'updateSheetTitles' || control === 'sheetTitleExists') {
              if ((firstSheetObject.get(control)?.get('type') as any)?.getRawValue()?.includes(fileType)) {
                firstSheetObject.get(control)?.get('show')?.setValue(value)
              }
            }
          })
        }
        break;
        case "video":
          Object.keys(this.multimediaUploadFormGroup.controls).forEach(control => {
            if (!Array.isArray(this.multimediaUploadFormGroup.get(control)?.value)) {
              if (((this.multimediaUploadFormGroup.get(control) as FormGroup)?.get('type') as any)?.getRawValue()?.includes(fileType)) {
                this.multimediaUploadFormGroup.get(control)?.get('show')?.setValue(value)
              }
            }
          })
          break;
    }
  }

  removeFormGroupFile(indexToRemove: number, formGroupName: AcceptedFileType) {
    let targetFormGroup: FormGroup | null = null;
    switch (formGroupName) {
      case AcceptedFileType.DOCUMENT:
        targetFormGroup = this.fileUploadFormGroup;
        break;
      case AcceptedFileType.MULTIMEDIA:
        targetFormGroup = this.multimediaUploadFormGroup;
        break;
      case AcceptedFileType.TABLE:
        targetFormGroup = this.excelUploadFormGroup;
        break;
      case AcceptedFileType.ORGANIZATION:
        targetFormGroup = this.organizationUploadFormGroup;
        break;
      default:
        this.toastService.error(this.localizationService.getLocalizationValue("FileUploadFormWizard.enterAValidForm"))
        return;
    }
    const filesControl = targetFormGroup.get('uploadedFiles') as FormControl;
    const currentFiles = [...(filesControl.value || [])];
    if (indexToRemove >= 0 && indexToRemove < currentFiles.length) {
      currentFiles.splice(indexToRemove, 1);
      filesControl.setValue(currentFiles);
      filesControl.updateValueAndValidity();

      // Dosya silindikten sonra form validasyonunu güncelle
      targetFormGroup.updateValueAndValidity();

      // Form validasyonunu manuel olarak tetikle
      setTimeout(() => {
        targetFormGroup?.updateValueAndValidity();
      }, 0);
    }
  }

  resetStepper($event, stepper: MatStepper) {
    if (this.resettingStepper) {
      return;
    }
    if ($event.selectedIndex === 0 && $event.previouslySelectedIndex !== 0) {
      this.resettingStepper = true;
      this.selectedFileType = null;
      stepper.reset();
      this.setDefaults();
      setTimeout(() => {
        this.resettingStepper = false;
      }, 0);
    }
  }

  moveToSecondStep(stepper: MatStepper) {
    this.selectedFileType === AcceptedFileType.API || this.selectedFileType === AcceptedFileType.URL ?
      this.toastService.info(this.localizationService.getLocalizationValue("FileUploadFormWizard.featureNotAvailable"))
      : this.selectedFileType && stepper.next();
    this.setDefaults();

    // Web Sitesi seçildiğinde URL input alanını focusla
    if (this.selectedFileType === AcceptedFileType.SCRAPER) {
      setTimeout(() => {
        const urlInput = document.querySelector('input[formControlName="url"]') as HTMLInputElement;
        if (urlInput) {
          urlInput.focus();
        }
      }, 100);
    }
  }

  setDefaults(){
    switch (this.selectedFileType) {
      case AcceptedFileType.DOCUMENT:
        this.fileUploadFormGroup.patchValue({
          uploadedFiles: [],
          makeOcrForImages: {
            type: ['pdf'],
            show: false,
            value: this.userDecisions.makeOcrForImages.value,
          },
          makeOcrForTables: {
            type: ['pdf'],
            show: false,
            value: this.userDecisions.makeOcrForTables.value,
          },
          saveTablesAsImages: {
            type: ['pdf'],
            show: false,
            value: this.userDecisions.saveTablesAsImages.value,
          },
        })
        break;
      case AcceptedFileType.MULTIMEDIA:
        this.multimediaUploadFormGroup.patchValue({
          uploadedFiles: [],
          hasTranscript: {
            type: ['video'],
            show: false,
            value: this.userDecisions.hasTranscript.value,
          },
          includeImportantMoments: {
            type: ['video'],
            show: false,
            value: this.userDecisions.includeImportantMoments.value,
          },
        })
        break;
      case AcceptedFileType.SCRAPER:
        this.urlUploadFormGroup.patchValue({
          url: '',
          language: this.userDecisions.language.value,
          length_low: this.userDecisions.lengthLow.value,
          length_high: this.userDecisions.lengthHigh.value,
          stop_words_low: this.userDecisions.stopWordsLow.value,
          stop_words_high:  this.userDecisions.stopWordsHigh.value,
          mode: this.userDecisions.mode.value,
          exclude_lines: this.userDecisions.excludeLines.value,
          include_images: this.userDecisions.includeImages.value,
          include_formatting: this.userDecisions.includeFormatting.value,
        });
        break;
      case AcceptedFileType.TABLE:
        // FormArray'i temizle ve yeniden oluştur
        const sheetsArray = this.excelUploadFormGroup.get('sheets') as FormArray;
        sheetsArray.clear();

        const defaultSheet = this._formBuilder.group({
          sheetIndex: [1],
          updateSheetTitles: this._formBuilder.group({
            type: [['excel']],
            show: [false],
            value: [true],
          }),
          sheetTitleExists: this._formBuilder.group({
            type: [['excel']],
            show: [false],
            value: [true],
          }),
          sheetHeaders: this._formBuilder.array([])
        });

        sheetsArray.push(defaultSheet);

        this.excelUploadFormGroup.patchValue({
          uploadedFiles: []
        });
        break;
      case AcceptedFileType.ORGANIZATION:
        this.organizationUploadFormGroup.setControl('columnMappings', this._formBuilder.group({}));
        this.organizationUploadFormGroup.patchValue({
          uploadedFiles: [],
          columnMappings: {},
          divisionType: null,
          divisionColumns: [],
          organizationSheetName: ''
        })
        break;
      case AcceptedFileType.QA:
        this.qaUploadFormGroup.patchValue({
          qaData: [] as QuestionAnswer[]
        })
        break;
    }
  }

  selectFileType(fileType: AcceptedFileType) {
    this.selectedFileType = fileType;
    this.fileTypeFormGroup.get('fileTypeSelection')?.setValue(fileType);
  }

  changeScraperOptions($event, key) {
    this.urlUploadFormGroup.get(key).setValue($event.checked);
  }

  updateSheetHeaderKey(sheetIndex: number, columnIndex: number, value: string){
    if (this.selectedFileType === AcceptedFileType.TABLE) {
      const sheetsArray = this.excelUploadFormGroup.get('sheets') as FormArray;
      if (sheetsArray && sheetsArray.length > sheetIndex) {
        const sheetHeaders = sheetsArray.at(sheetIndex).get('sheetHeaders') as FormArray;
        if (sheetHeaders && sheetHeaders.length > columnIndex) {
          sheetHeaders.at(columnIndex).get('key')?.setValue(value || '');
        }
      }
    }
  }

  addColumnMappingToOrganizationForm(key: string, fieldName: string, type: string, dateType: string){
    const columnMappingsFormGroup = this.organizationUploadFormGroup.get('columnMappings').getRawValue()
    const existingControl = columnMappingsFormGroup[key]
    if (existingControl) {
      if (dateType && existingControl.type === 'DATE') {
        type = `DATE|${dateType}`
      }
      const updatedFieldName = fieldName ?? existingControl.field_name
      const updatedType = type ?? existingControl.type
      columnMappingsFormGroup[key]['field_name'] = updatedFieldName
      columnMappingsFormGroup[key]['type'] = updatedType
      this.organizationUploadFormGroup.get('columnMappings').patchValue(columnMappingsFormGroup);
    } else {
      (this.organizationUploadFormGroup.get('columnMappings') as FormGroup).addControl(
        key,
        this._formBuilder.group({
          field_name: [fieldName],
          type: [type]
        })
      );
    }
  }

  updateDivisionColumns(existingFieldName: string) {
    const divisionType = this.organizationUploadFormGroup.get('divisionType')?.getRawValue();
    if (divisionType !== 'PARENT_CHILD' && divisionType !== 'COLUMN') {
      this.toastService.info(this.localizationService.getLocalizationValue("FileUploadFormWizard.pickADivisionType"))
      return;
    }
    const existingIndex = (this.organizationUploadFormGroup.get('divisionColumns') as FormArray).controls
      .findIndex(control => control.get('existingFieldName')?.value === existingFieldName);
    if (existingIndex !== -1) {
      (this.organizationUploadFormGroup.get('divisionColumns') as FormArray).removeAt(existingIndex);
      this.reOrderDivisionColumns();
      return;
    }
    if (divisionType === 'PARENT_CHILD' && (this.organizationUploadFormGroup.get('divisionColumns') as FormArray).length >= 2) {
      this.toastService.error(this.localizationService.getLocalizationValue("FileUploadFormWizard.parentDivisionTypeError"))
      return;
    }
    const newDivisionColumn = this._formBuilder.group({
      existingFieldName: [existingFieldName],
      divisionOrder: [((this.organizationUploadFormGroup.get('divisionColumns') as FormArray).length + 1).toString()],
    });
    (this.organizationUploadFormGroup.get('divisionColumns') as FormArray).push(newDivisionColumn);
  }

  reOrderDivisionColumns() {
    (this.organizationUploadFormGroup.get('divisionColumns') as FormArray).controls.forEach((control, i) => {
      control.get('divisionOrder').setValue(i + 1);
    })
  }

  onQaDataChange(qaData: QuestionAnswer[]) {
    this.qaUploadFormGroup.get('qaData')?.setValue(qaData);
    this.qaUploadFormGroup.updateValueAndValidity();
  }

  checkFormIsValid() {
    const formGroup = this.pickFormGroup(this.selectedFileType)
    if (formGroup) {
      // Form validasyonunu manuel olarak tetikle
      formGroup.updateValueAndValidity();

      // Tüm form kontrollerini kontrol et
      this.markFormGroupTouched(formGroup);

      const invalidControls = this.findInvalidControls(formGroup);
      if (invalidControls.length > 0) {
        invalidControls.forEach((control) => {
          if (control === 'divisionType') {
            this.toastService.info(this.localizationService.getLocalizationValue("FileUploadFormWizard.divisionTypeError"))
          }
          if (control === 'uploadedFiles') {
            this.toastService.info(this.localizationService.getLocalizationValue("FileUploadFormWizard.uploadedFileError"))
          }
          if (control === 'url') {
            this.toastService.info(this.localizationService.getLocalizationValue("FileUploadFormWizard.scraperUrlError"))
          }
          if (control === 'qaData') {
            this.toastService.info(this.localizationService.getLocalizationValue("FileUploadFormWizard.qaDataError"))
          }
        })
        return false; // Form geçersiz
      }
    }
    return true; // Form geçerli
  }

  markFormGroupTouched(formGroup: FormGroup | FormArray) {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      if (control instanceof FormGroup || control instanceof FormArray) {
        this.markFormGroupTouched(control);
      } else if (control) {
        control.markAsTouched();
      }
    });
  }

  validateAndProceed(stepper: MatStepper) {
    if (this.checkFormIsValid()) {
      stepper.next();
    }
  }

  findInvalidControls(formGroup: FormGroup | FormArray): string[] {
    const invalid: string[] = [];

    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);

      if (control instanceof FormGroup || control instanceof FormArray) {
        invalid.push(...this.findInvalidControls(control));
      } else if (control && control.invalid) {
        invalid.push(key);
      }
    });

    return invalid;
  }

  httpsValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (value && !value.startsWith('https://')) {
        return { invalidProtocol: true };
      }
      return null;
    };
  }

  prependHttpsIfMissing() {
    const control = this.urlUploadFormGroup.get('url');
    const value = control?.value;

    if (value && !value.startsWith('https://')) {
      control?.setValue(`https://${value}`);
      control?.updateValueAndValidity();
    }
  }

  setCustomMimeType(file) {
    const extension = file.name.split('.').pop()?.toLowerCase();

    const mimeTypeMap = {
      'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'xls': 'application/vnd.ms-excel',
      'csv': 'text/csv',
      'ods': 'application/vnd.oasis.opendocument.spreadsheet',

      'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'doc': 'application/msword',
      'odt': 'application/vnd.oasis.opendocument.text',

      'png': 'image/png',
      'jpeg': 'image/jpeg',
      'jpg': 'image/jpg',
      'mp4': 'video/mp4',
      'avi': 'video/x-msvideo',
      'mpeg': 'video/mpeg',
      'ogv': 'video/ogg',
      'webm': 'video/webm',
      'avif': 'image/avif',
      'bmp': 'image/bmp',
      'svg': 'image/svg+xml',
      'tiff': 'image/tiff',
      'webp': 'image/webp',

      'pdf': 'application/pdf',

      'txt': 'text/plain',
      'md': 'text/markdown',
      'markdown': 'text/x-markdown',
    };

    return mimeTypeMap[extension] || 'application/octet-stream';
  }

  async sendData(stepper: MatStepper, selectedFileType: AcceptedFileType) {
    this.isFileUploading = true;
    const formData = new FormData();
    let data: any = null;
    let files: File[] | null = null;
    let uploadSuccessful = true;

    const toast = this.toastService.info(
      this.localizationService.getLocalizationValue("FileUploadFormWizard.filesUploading"),
      this.localizationService.getLocalizationValue("FileUploadFormWizard.pleaseWait"), {
      timeOut: 0,
      extendedTimeOut: 0,
      closeButton: false,
      tapToDismiss: false,
    });

    switch (selectedFileType) {
      case AcceptedFileType.DOCUMENT:
        const documentForm = this.fileUploadFormGroup.getRawValue();
        data = {
          makeOcrForImages: documentForm.makeOcrForImages.value,
          makeOcrForTables: documentForm.makeOcrForTables.value,
          saveTablesAsImages: documentForm.saveTablesAsImages.value,
        };
        files = documentForm.uploadedFiles;
        break;
      case AcceptedFileType.SCRAPER:
        const scraperForm = this.urlUploadFormGroup.getRawValue();
        data = {
          excludeLines: scraperForm.exclude_lines,
          includeFormatting: scraperForm.include_formatting,
          includeImages: scraperForm.include_images,
          language: scraperForm.language,
          lengthHigh: scraperForm.length_high,
          lengthLow: scraperForm.length_low,
          mode: scraperForm.mode,
          stopWordsHigh: scraperForm.stop_words_high,
          stopWordsLow: scraperForm.stop_words_low,
          url: scraperForm.url,
        };
        break;
      case AcceptedFileType.MULTIMEDIA:
        const multimediaForm = this.multimediaUploadFormGroup.getRawValue();
        data = {
          hasTranscript: multimediaForm.hasTranscript.value,
          includeImportantMoments: multimediaForm.includeImportantMoments.value,
        };
        files = multimediaForm.uploadedFiles;
        break;
      case AcceptedFileType.TABLE:
        const excelForm = this.excelUploadFormGroup.getRawValue();
        const cleanedSheets = excelForm.sheets.map((sheet: any) => {
          const hasUpdateSheetTitles = sheet.updateSheetTitles.value;
          const hasSheetTitles = sheet.sheetTitleExists.value;
          return {
            ...sheet,
            updateSheetTitles: hasUpdateSheetTitles,
            sheetTitleExists: hasSheetTitles,
            sheetHeaders: hasUpdateSheetTitles ? sheet.sheetHeaders : [],
          };
        });
        data = { sheets: cleanedSheets };
        // console.log("Excel datası: ",data)
        files = excelForm.uploadedFiles;
        break;
      case AcceptedFileType.ORGANIZATION:
        const organizationForm = this.organizationUploadFormGroup.getRawValue();
        data = {
          columnMappings: organizationForm.columnMappings,
          divisionColumns: organizationForm.divisionColumns,
          divisionType: organizationForm.divisionType,
          organizationSheetName: organizationForm.organizationSheetName,
        };
        files = organizationForm.uploadedFiles;
        break;
      case AcceptedFileType.QA:
        const qaForm = this.qaUploadFormGroup.getRawValue();
        data = {
          qaData: qaForm.qaData,
        };
        files = null; // QA dosya gerektirmez, sadece data
        break;
    }

    formData.append('optionsPayload', JSON.stringify(data));
    formData.append('uploadedFileType', selectedFileType.toUpperCase());
    formData.append('requestedName', ''); // TODO: RequestedName eklenmeli.
    formData.append("organisationId", this.selectedOrganisationId.toString())

    if (files && files.length > 0) {
      const uploadPromises = files.map(async (file) => {
        formData.set('uploadedFile', file);
        const mimeType = this.setCustomMimeType(file);
        formData.set("fileMimeType", mimeType);
        let retryCount = 0;
        const maxRetries = 1;
        while (retryCount <= maxRetries) {
          try {
            const response: any = await firstValueFrom(this.fileUploadService.sendFile(formData));
            if (response?.exception) {
              this.toastService.error(`${this.localizationService.getLocalizationValue("FileUploadFormWizard.fileUploadError")}${file.name}`);
              uploadSuccessful = false;
            } else {
              this.toastService.success(`${this.localizationService.getLocalizationValue("FileUploadFormWizard.fileUploaded")}${file.name}`);
            }
            break;
          } catch (error) {
            retryCount++;
            if (retryCount > maxRetries) {
              this.toastService.error(`${this.localizationService.getLocalizationValue("FileUploadFormWizard.errorOccurred")}${file.name}.`);
              uploadSuccessful = false;
            }
          }
        }
      });

      await Promise.all(uploadPromises);
    } else if (selectedFileType === AcceptedFileType.SCRAPER && data?.url) {
      this.fileUploadService.sendFile(formData).subscribe({
        next: (res: any) => {
          if (res?.exception) {
            this.toastService.clear(toast.toastId);
            this.toastService.error(this.localizationService.getLocalizationValue("FileUploadFormWizard.urlUploadFailed"));
            uploadSuccessful = false;
          } else {
            this.toastService.clear(toast.toastId);
            this.toastService.success(this.localizationService.getLocalizationValue("FileUploadFormWizard.urlUploaded"));
          }
        },
        error: () => {
          this.toastService.clear(toast.toastId);

          this.toastService.error(this.localizationService.getLocalizationValue("FileUploadFormWizard.urlUploadFailed"));
          uploadSuccessful = false;
        },
      });
    } else if (selectedFileType === AcceptedFileType.QA && data?.qaData && data.qaData.length > 0) {
      // QA için requestedName'i ayarla
      formData.set('requestedName', 'qa');
      this.fileUploadService.sendFile(formData).subscribe({
        next: (res: any) => {
          if (res?.exception) {
            this.toastService.clear(toast.toastId);
            this.toastService.error(this.localizationService.getLocalizationValue("FileUploadFormWizard.qaUploadFailed"));
            uploadSuccessful = false;
          } else {
            this.toastService.clear(toast.toastId);
            this.toastService.success(this.localizationService.getLocalizationValue("FileUploadFormWizard.urlUploadFailed"));
          }
        },
        error: () => {
          this.toastService.clear(toast.toastId);
          this.toastService.error(this.localizationService.getLocalizationValue("FileUploadFormWizard.qaUploadFailed"));
          uploadSuccessful = false;
        },
      });
    } else {
      this.toastService.clear(toast.toastId);

      this.toastService.warning(this.localizationService.getLocalizationValue("FileUploadFormWizard.notFound"));
      this.isFileUploading = false;
      return;
    }

    this.isFileUploading = false;
    this.toastService.clear(toast.toastId);
    if (uploadSuccessful) {
      stepper.reset();
    }
  }

  protected readonly AcceptedFileType = AcceptedFileType;
}
